import rnnoiseWorklet from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url'
import rnnoiseWasm from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url'
import rnnoiseSimd from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url'
import gtcrnWorklet from '@sapphi-red/web-noise-suppressor/gtcrnWorklet.js?url'
import gtcrnWasm from '@sapphi-red/web-noise-suppressor/gtcrn.wasm?url'
import type { AudioSettings } from './audio-settings'

export interface VoiceMeter { db: number; threshold: number; open: boolean }
type NeuralNode = AudioWorkletNode & { destroy(): void }
const binaries = new Map<string, Promise<ArrayBuffer>>()
function binary(mode: string): Promise<ArrayBuffer> {
  if (!binaries.has(mode)) {
    const loading = import('@sapphi-red/web-noise-suppressor').then((lib) => mode === 'gtcrn'
      ? lib.loadGtcrn({ url: gtcrnWasm }) : lib.loadRnnoise({ url: rnnoiseWasm, simdUrl: rnnoiseSimd }))
      .then(async (bytes) => { await WebAssembly.compile(bytes); return bytes })
      .catch((e) => { binaries.delete(mode); throw e })
    binaries.set(mode, loading)
  }
  return binaries.get(mode)!
}

/** LiveKit processor. A dedicated 48 kHz context resamples hardware at 44.1 kHz
 * before neural inference; it never connects the microphone to the speakers. */
export class VoiceAudioProcessor {
  name = 'atrium-voice'
  processedTrack?: MediaStreamTrack
  private ctx?: AudioContext
  private source?: MediaStreamAudioSourceNode
  private destination?: MediaStreamAudioDestinationNode
  private neural?: NeuralNode
  private envelope?: AudioWorkletNode
  private input?: GainNode
  private highPass?: BiquadFilterNode
  private hum?: BiquadFilterNode
  private clarity?: BiquadFilterNode
  private compressor?: DynamicsCompressorNode
  private limiter?: DynamicsCompressorNode
  private transmit = true
  private destroyed = false
  private failedNeural = false
  private lifecycle = 0

  constructor(private settings: () => AudioSettings,
    private meter: (value: VoiceMeter) => void = () => {},
    private status: (value: string) => void = () => {}) {}

  async init({ track }: { track: MediaStreamTrack; audioContext?: AudioContext }) {
    const lifecycle = ++this.lifecycle
    this.destroyed = false
    const s = this.settings()
    const ctx = this.ctx = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' })
    this.source = ctx.createMediaStreamSource(new MediaStream([track]))
    this.destination = ctx.createMediaStreamDestination()
    this.destination.channelCount = s.quality === 'music' ? 2 : 1
    this.input = ctx.createGain()
    this.highPass = ctx.createBiquadFilter(); this.highPass.type = 'highpass'
    this.hum = ctx.createBiquadFilter(); this.hum.type = 'notch'; this.hum.Q.value = 20
    this.clarity = ctx.createBiquadFilter(); this.clarity.type = 'peaking'; this.clarity.frequency.value = 3000; this.clarity.Q.value = 0.8
    this.compressor = ctx.createDynamicsCompressor()
    this.limiter = ctx.createDynamicsCompressor()
    this.limiter.threshold.value = -1; this.limiter.knee.value = 0; this.limiter.ratio.value = 20
    this.limiter.attack.value = 0.001; this.limiter.release.value = 0.08
    try {
      if (!ctx.audioWorklet) throw new Error('AudioWorklet unavailable')
      await ctx.audioWorklet.addModule('/audio/voice-worklet.js')
      if (lifecycle !== this.lifecycle) throw new Error('Microphone processor stopped')
      this.envelope = new AudioWorkletNode(ctx, 'atrium-voice', { outputChannelCount: [s.quality === 'music' ? 2 : 1] })
      this.envelope.port.onmessage = ({ data }) => { if (!this.destroyed) this.meter(data) }
    } catch {
      if (lifecycle !== this.lifecycle) throw new Error('Microphone processor stopped')
      this.status('Voice detection is unavailable in this browser. Use an open microphone or push-to-talk.')
    }
    if (s.noiseSuppression === 'rnnoise' || s.noiseSuppression === 'gtcrn') {
      try {
        const lib = await import('@sapphi-red/web-noise-suppressor')
        const bytes = await binary(s.noiseSuppression)
        if (lifecycle !== this.lifecycle) throw new Error('Microphone processor stopped')
        await ctx.audioWorklet.addModule(s.noiseSuppression === 'gtcrn' ? gtcrnWorklet : rnnoiseWorklet)
        if (lifecycle !== this.lifecycle) throw new Error('Microphone processor stopped')
        this.neural = s.noiseSuppression === 'gtcrn'
          ? new lib.GtcrnWorkletNode(ctx, { wasmBinary: bytes, maxChannels: 2 })
          : new lib.RnnoiseWorkletNode(ctx, { wasmBinary: bytes, maxChannels: 2 })
        this.neural.onprocessorerror = () => {
          if (this.destroyed) return
          this.failedNeural = true
          this.status('Neural filtering stopped. Select Browser filtering to continue with noise suppression.')
          this.connectGraph()
        }
        this.status(s.noiseSuppression === 'gtcrn' ? 'GTCRN neural filtering active' : 'RNNoise neural filtering active')
      } catch (e) {
        if (lifecycle !== this.lifecycle) throw new Error('Microphone processor stopped')
        this.failedNeural = true
        this.status('Neural filtering could not load. Select Browser filtering or retry the neural filter.')
        console.warn('[audio] neural filter', e)
      }
    } else if (this.envelope) this.status(s.noiseSuppression === 'browser' ? 'Browser noise suppression' : 'Noise suppression off')
    this.connectGraph()
    this.update()
    this.processedTrack = this.destination.stream.getAudioTracks()[0]
    await ctx.resume()
  }

  private connectGraph() {
    const nodes = [this.source, this.highPass, this.hum, this.neural, this.envelope, this.clarity, this.compressor, this.input, this.limiter, this.destination]
    for (const node of nodes) node?.disconnect()
    const chain = nodes.filter((n) => n && !(n === this.neural && this.failedNeural)) as AudioNode[]
    for (let i = 0; i < chain.length - 1; i++) chain[i].connect(chain[i + 1])
  }

  update() {
    if (!this.ctx) return
    const s = this.settings(), t = this.ctx.currentTime
    this.highPass?.frequency.setTargetAtTime(s.highPass ? 85 : 0, t, 0.01)
    this.hum?.frequency.setTargetAtTime(s.humFilter === 'off' ? 0 : Number(s.humFilter), t, 0.01)
    this.clarity?.gain.setTargetAtTime(s.voiceClarity, t, 0.01)
    if (this.compressor) {
      this.compressor.threshold.value = s.compressor ? -24 : 0
      this.compressor.knee.value = 12; this.compressor.ratio.value = s.compressor ? 3 : 1
      this.compressor.attack.value = 0.006; this.compressor.release.value = 0.15
    }
    if (this.limiter) this.limiter.ratio.value = s.limiter ? 20 : 1
    this.setTransmit(this.transmit)
  }

  setTransmit(on: boolean) {
    this.transmit = on
    const s = this.settings()
    // Pinia preferences are a Vue proxy. Worklet messages must contain a plain
    // snapshot; every AudioSettings field is a primitive value.
    this.envelope?.port.postMessage({ settings: { ...s }, transmit: on })
    const allowed = this.envelope ? true : on && s.voiceMode !== 'activity'
    this.input?.gain.setTargetAtTime(allowed ? s.inputVolume / 100 : 0, this.ctx?.currentTime || 0, 0.005)
  }

  async restart(options: { track: MediaStreamTrack; audioContext?: AudioContext }) {
    await this.destroy(); await this.init(options)
  }

  async destroy() {
    this.lifecycle++
    this.destroyed = true
    this.meter({ db: -100, threshold: -48, open: false })
    this.neural?.destroy(); this.neural?.port.close(); this.envelope?.port.close()
    for (const node of [this.source, this.neural, this.envelope, this.highPass, this.hum, this.clarity, this.compressor, this.input, this.limiter, this.destination]) node?.disconnect()
    this.processedTrack?.stop()
    this.processedTrack = undefined; this.neural = undefined; this.envelope = undefined; this.failedNeural = false
    const ctx = this.ctx; this.ctx = undefined
    if (ctx && ctx.state !== 'closed') await ctx.close()
  }
}
